import { useState, ReactNode } from 'react'
import capitalize from 'lodash/capitalize'
import { useDispatch } from 'react-redux'
import axios from 'shared/utils/axios'
import { showSuccess, showError } from 'shared/redux/app/reducer'
import { AxiosResponse, AxiosError } from 'axios'
import { LOGIN_WAIVER_ERROR_MESSAGE } from 'shared/constants/user'
import { randomNumber } from 'shared/utils/tools'

interface MutationParams {
  data?: any
  method?: 'POST' | 'PUT' | 'DELETE'
  onSuccess?: Function
  onError?: Function
  hideDialog?: boolean
  message?: string | ReactNode
  messageBody?: ReactNode
  subText?: string
  onMessageDismiss?(): void
  url: string
  token?: string
  isBase?: boolean
  isMulti?: boolean
  autohideTimeout?: number
  onBatchSuccess?: Function
  onBatchInitialSuccess?: Function
  onBatchFinalSuccess?: Function
  batchSize?: number
}

interface NodeMutation extends Omit<MutationParams, 'url'> {
  id?: string
  node: string
}

export type MutationFunctionType = (params?: Omit<MutationParams, 'url'>) => Promise<AxiosResponse<any>>

export default function useMutation(params: MutationParams) {
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const [data, setData] = useState()
  const [error, setError] = useState()

  const state = {
    loading,
    data,
    error,
  }
  async function mutate(params2: Omit<MutationParams, 'url'> = {}) {
    const allParams = { ...params, ...params2 }
    const {
      data: body,
      method = 'POST',
      onSuccess = () => {},
      onError = () => {},
      onMessageDismiss,
      message,
      messageBody,
      subText,
      hideDialog = true,
      isBase = false,
      isMulti = false,
      autohideTimeout,
    } = allParams

    if (!isMulti) setLoading(true)

    let { url } = allParams
    const needsId = ['put', 'delete'].includes(method.toLowerCase())
    if (needsId && !Array.isArray(body) && body?.id) {
      url = `${url}/${body.id}`
    }
    url = [isBase && 'base', url].filter(Boolean).join('')
    const response: any = await axios({
      data: body,
      method,
      url,
    }).catch((err: AxiosError) => {
      let subtext = '',
        isShowSubtext = false
      if (err.response?.data.code !== 'BadRequest' && err.response?.data.message !== LOGIN_WAIVER_ERROR_MESSAGE) {
        dispatch(
          showError({
            message: err.response?.data.message,
            subText: subtext,
            showSubText: isShowSubtext,
          }),
        )
      }
      setData(null)
      onError(err.response?.data)
      setError(err.response?.data)
      return { error: err.response?.data }
    })
    if (!isMulti) setLoading(false)
    if (response && !response.error) {
      setError(null)
      setData(response)
      onSuccess(response)
      if (message) {
        dispatch(
          showSuccess({ message, hideDialog, messageBody, subText, autohideTimeout, onDismiss: onMessageDismiss }),
        )
      }
    }
    return response
  }

  return [state, mutate] as const
}

export function useCreateMultiNode(defaultParams: NodeMutation) {
  const {
    node,
    message = `${capitalize(defaultParams.node)} created`,
    isBase = true,
    onSuccess: defaultOnSuccess,
    onBatchSuccess = () => {},
    onBatchInitialSuccess = () => {},
    onBatchFinalSuccess = () => {},
    batchSize = 2,
  } = defaultParams
  const [mutationState, mutate] = useMutation({ method: 'POST', url: `/${node}`, isBase })
  const [progress, setProgress] = useState({ total: 0, completed: 0, percentage: 0 })
  const [loading, setLoading] = useState(false)

  async function createMultiple(params: NodeMutation & { data: any[] }) {
    const {
      data: list,
      isBase: batchIsBase = isBase,
      onSuccess,
      onBatchSuccess = () => {},
      onBatchInitialSuccess = () => {},
      onBatchFinalSuccess = () => {},
      batchSize = 2,
      onError,
      message: batchMessage,
    } = params
    if (!list || list.length === 0) return { results: [], errors: [] }

    const BATCH_SIZE = batchSize
    const batches = []
    const totalItems = list.length

    setProgress({ total: totalItems, completed: 0, percentage: 0 })
    setLoading(true)

    for (let i = 0; i < totalItems; i += BATCH_SIZE) {
      batches.push(list.slice(i, i + BATCH_SIZE))
    }

    const results: any[] = []
    const errors: any[] = []
    let idx = 0
    for (const batch of batches) {
      const res = await mutate({
        data: { batch: batch },
        isMulti: true,
        isBase: batchIsBase,
        onSuccess: idx === 0 ? onBatchInitialSuccess : onSuccess,
        onError,
        message: batchMessage || message,
      })

      if (res?.error) errors.push(res.error)
      else {
        results.push(res)
        await onBatchSuccess(res)
      }
      idx++

      // Update progress
      setProgress(prev => {
        const completed = prev.completed + batch.length
        return { total: prev.total, completed, percentage: Math.round((completed / prev.total) * 100) }
      })

      // <-- ADD gap here before next batch
      await new Promise(resolve => setTimeout(resolve, randomNumber(1500, 2000)))
    }

    setLoading(false)

    if (defaultOnSuccess) defaultOnSuccess({ results, errors })
    if (onSuccess) onSuccess({ results, errors })
    if (onBatchFinalSuccess) onBatchFinalSuccess({ results, errors })

    return { results, errors }
  }

  return [{ ...mutationState, progress, loading }, createMultiple] as const
}

export function useCreateNode(params: NodeMutation) {
  const { node, message = `${capitalize(node)} successfully created`, isBase = true } = params
  return useMutation({
    message,
    method: 'POST',
    url: `/${node}`,
    isBase,
    ...params,
  })
}

export function useUpdateNode(params: NodeMutation) {
  const { node, message = `${capitalize(node)} successfully updated` } = params
  return useMutation({
    message,
    method: 'PUT',
    url: `/${node}`,
    isBase: true,
    ...params,
  })
}

export function useDeleteNode(params: NodeMutation) {
  const { node, message = `${capitalize(node)} successfully deleted` } = params
  return useMutation({
    message,
    method: 'DELETE',
    url: `/${node}`,
    isBase: true,
    ...params,
  })
}
